import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Divider, LinearProgress, Button } from '@mui/material';
import {
  Build as RepairIcon,
  LocalGasStation as FuelIcon,
  Shield as ShieldIcon,
  Security as HullIcon,
  Warning as WarningIcon,
  CheckCircle as OkIcon,
} from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import { useChatContext } from '../../contexts/ChatContext';
import { useConsole } from '../../contexts/ConsoleContext';
import type { Ship, Station } from '../../types/gameState';

interface RepairsPanelProps {
  playerId: string;
}

export function RepairsPanel({ playerId }: RepairsPanelProps) {
  const [ship, setShip] = useState<Ship | null>(null);
  const [station, setStation] = useState<Station | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sendMessage } = useChatContext();
  const { setSelectedPanel } = useConsole();

  useEffect(() => {
    async function fetchShipAndStation() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch player to get current station
        const playerLogId = logService.api('GET', `/api/players/${playerId}`, {}, 'pending');
        const playerStart = Date.now();
        const playerRes = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}`);
        const playerDuration = Date.now() - playerStart;

        if (!playerRes.ok) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: `HTTP ${playerRes.status}` });
          setError('Failed to load player data');
          setIsLoading(false);
          return;
        }

        const playerResult = await playerRes.json();
        if (!playerResult.success || !playerResult.data) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: 'No player data' });
          setError('No player data available');
          setIsLoading(false);
          return;
        }

        logService.updateLog(playerLogId, { status: 'success', duration: playerDuration });
        const player = playerResult.data;

        // Fetch player's ships
        const shipsLogId = logService.api('GET', `/api/players/${playerId}/ships`, {}, 'pending');
        const shipsStart = Date.now();
        const shipsRes = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}/ships`);
        const shipsDuration = Date.now() - shipsStart;

        if (shipsRes.ok) {
          const shipsResult = await shipsRes.json();
          if (shipsResult.success && shipsResult.data) {
            logService.updateLog(shipsLogId, { status: 'success', duration: shipsDuration });
            const ships = shipsResult.data;
            const activeShip = ships.find((s: Ship) => !s.is_destroyed);

            if (activeShip) {
              // Fetch full ship details
              const shipLogId = logService.api('GET', `/api/ships/${activeShip.id}`, {}, 'pending');
              const shipStart = Date.now();
              const shipRes = await fetch(`${DATABASE_SERVICE_URL()}/api/ships/${activeShip.id}`);
              const shipDuration = Date.now() - shipStart;

              if (shipRes.ok) {
                const shipResult = await shipRes.json();
                if (shipResult.success && shipResult.data) {
                  logService.updateLog(shipLogId, { status: 'success', duration: shipDuration });
                  setShip(shipResult.data);
                } else {
                  logService.updateLog(shipLogId, { status: 'error', duration: shipDuration, message: 'No ship data' });
                }
              } else {
                logService.updateLog(shipLogId, { status: 'error', duration: shipDuration, message: `HTTP ${shipRes.status}` });
              }
            }
          } else {
            logService.updateLog(shipsLogId, { status: 'error', duration: shipsDuration, message: 'No ships data' });
          }
        } else {
          logService.updateLog(shipsLogId, { status: 'error', duration: shipsDuration, message: `HTTP ${shipsRes.status}` });
        }

        // Fetch station if docked
        if (player.current_station_id) {
          const stationLogId = logService.api('GET', `/api/stations/${player.current_station_id}`, {}, 'pending');
          const stationStart = Date.now();
          const stationRes = await fetch(`${DATABASE_SERVICE_URL()}/api/stations/${player.current_station_id}`);
          const stationDuration = Date.now() - stationStart;

          if (stationRes.ok) {
            const stationResult = await stationRes.json();
            if (stationResult.success && stationResult.data) {
              logService.updateLog(stationLogId, { status: 'success', duration: stationDuration });
              setStation(stationResult.data);
            } else {
              logService.updateLog(stationLogId, { status: 'error', duration: stationDuration, message: 'No station data' });
            }
          } else {
            logService.updateLog(stationLogId, { status: 'error', duration: stationDuration, message: `HTTP ${stationRes.status}` });
          }
        }
      } catch (err) {
        setError('Failed to load ship and station data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchShipAndStation();
  }, [playerId]);

  if (isLoading) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="rectangular" height={150} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="error" align="center">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!ship) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                No ship data available
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const FUEL_COST_PER_UNIT = 5;
  const HULL_REPAIR_COST_PER_POINT = 20;
  const SHIELD_REPAIR_COST_PER_POINT = 15;

  const hullDamage = ship.hull_max - ship.hull_current;
  const shieldDamage = ship.shields_max - ship.shields_current;
  const fuelNeeded = ship.fuel_max - ship.fuel_current;

  const hullRepairCost = hullDamage * HULL_REPAIR_COST_PER_POINT;
  const shieldRepairCost = shieldDamage * SHIELD_REPAIR_COST_PER_POINT;
  const refuelCost = fuelNeeded * FUEL_COST_PER_UNIT;
  const totalRepairCost = hullRepairCost + shieldRepairCost;

  const hullPercent = (ship.hull_current / ship.hull_max) * 100;
  const shieldPercent = (ship.shields_current / ship.shields_max) * 100;
  const fuelPercent = (ship.fuel_current / ship.fuel_max) * 100;

  const needsRepair = hullDamage > 0 || shieldDamage > 0;
  const needsRefuel = fuelNeeded > 0;
  const canRepair = station?.has_repair;
  const canRefuel = station?.has_refuel;

  const handleRepairRefuel = () => {
    let message = 'Please ';
    if (needsRepair && canRepair && needsRefuel && canRefuel) {
      message += 'repair my ship and refuel it';
    } else if (needsRepair && canRepair) {
      message += 'repair my ship';
    } else if (needsRefuel && canRefuel) {
      message += 'refuel my ship';
    }

    setSelectedPanel('chat');
    sendMessage(message);
  };

  return (
    <Box className="panel">
      <Box className="panel-scroll">
        <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="overline" sx={{ color: 'warning.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem', display: 'block', mb: 1 }}>
              Ship Status & Repairs
            </Typography>

            {/* Ship Status */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Ship Condition
              </Typography>
              <Divider sx={{ my: 1 }} />

              {/* Hull */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HullIcon sx={{ fontSize: 16, color: hullPercent < 50 ? 'error.main' : 'success.main' }} />
                    <Typography variant="body2">Hull Integrity</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: hullPercent < 50 ? 'error.main' : 'success.main' }}>
                    {ship.hull_current}/{ship.hull_max} ({Math.round(hullPercent)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={hullPercent}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: hullPercent < 50 ? 'error.main' : hullPercent < 80 ? 'warning.main' : 'success.main',
                    },
                  }}
                />
              </Box>

              {/* Shields */}
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ShieldIcon sx={{ fontSize: 16, color: shieldPercent < 50 ? 'warning.main' : 'info.main' }} />
                    <Typography variant="body2">Shield Strength</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: shieldPercent < 50 ? 'warning.main' : 'info.main' }}>
                    {ship.shields_current}/{ship.shields_max} ({Math.round(shieldPercent)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={shieldPercent}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: shieldPercent < 50 ? 'warning.main' : 'info.main',
                    },
                  }}
                />
              </Box>

              {/* Fuel */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FuelIcon sx={{ fontSize: 16, color: fuelPercent < 25 ? 'error.main' : 'primary.main' }} />
                    <Typography variant="body2">Fuel Level</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: fuelPercent < 25 ? 'error.main' : 'primary.main' }}>
                    {ship.fuel_current}/{ship.fuel_max} ({Math.round(fuelPercent)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={fuelPercent}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: fuelPercent < 25 ? 'error.main' : fuelPercent < 50 ? 'warning.main' : 'primary.main',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Station Services */}
            {!station ? (
              <Box sx={{ textAlign: 'center', py: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                <WarningIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Dock at a station for repairs and refueling
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Station Services
                </Typography>
                <Divider sx={{ my: 1 }} />

                {/* Repair Service */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {canRepair ? (
                      <OkIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )}
                    <Typography variant="body2" color={canRepair ? 'text.primary' : 'text.disabled'}>
                      Hull & Shield Repair
                    </Typography>
                  </Box>
                  {canRepair ? (
                    needsRepair ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'warning.main', fontWeight: 600 }}>
                        ¤{totalRepairCost.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                        No damage
                      </Typography>
                    )
                  ) : (
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                      Unavailable
                    </Typography>
                  )}
                </Box>

                {/* Refuel Service */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {canRefuel ? (
                      <OkIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                    )}
                    <Typography variant="body2" color={canRefuel ? 'text.primary' : 'text.disabled'}>
                      Refueling
                    </Typography>
                  </Box>
                  {canRefuel ? (
                    needsRefuel ? (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'warning.main', fontWeight: 600 }}>
                        ¤{refuelCost.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                        Tank full
                      </Typography>
                    )
                  ) : (
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                      Unavailable
                    </Typography>
                  )}
                </Box>

                {/* Total Cost */}
                {(needsRepair || needsRefuel) && (canRepair || canRefuel) && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, borderRadius: 1, backgroundColor: 'action.hover' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Estimated Total
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main' }}>
                        ¤{((canRepair ? totalRepairCost : 0) + (canRefuel ? refuelCost : 0)).toLocaleString()}
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Action Button */}
                {(needsRepair || needsRefuel) && (canRepair || canRefuel) && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<RepairIcon />}
                    onClick={handleRepairRefuel}
                    sx={{ mt: 2 }}
                  >
                    {needsRepair && canRepair && needsRefuel && canRefuel
                      ? 'Repair & Refuel Ship'
                      : needsRepair && canRepair
                        ? 'Repair Ship'
                        : 'Refuel Ship'}
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
